/**
 * Odemis UI
 * Carruseles, buscador predictivo, contador regresivo, galería de la tarjeta,
 * copiar código, rotador de anuncios, header compacto y textos expandibles.
 */
(() => {
  'use strict';

  const mqDesktop = window.matchMedia('(min-width: 990px)');
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  const escapeHTML = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  let liveRegion;
  const announce = (message) => {
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'od-visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    requestAnimationFrame(() => (liveRegion.textContent = message));
  };

  /* -------------------------------------------------------------------
     Carrusel (scroll-snap nativo + flechas, puntos y autoplay opcional)
     ------------------------------------------------------------------- */
  class OdCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-od-track]');
      if (!this.track) {
        // Contenido que llega después (p. ej. recomendaciones): esperar a que aparezca el carril.
        if (!this.waitObserver) {
          this.waitObserver = new MutationObserver(() => {
            if (!this.querySelector('[data-od-track]')) return;
            this.waitObserver.disconnect();
            this.waitObserver = null;
            this.connectedCallback();
          });
          this.waitObserver.observe(this, { childList: true, subtree: true });
        }
        return;
      }
      if (this.ready) return;
      this.ready = true;
      this.loop = this.hasAttribute('data-loop');
      this.autoplayMs = parseFloat(this.dataset.autoplay || '0') * 1000;
      this.prevButtons = [...this.querySelectorAll('[data-od-prev]')];
      this.nextButtons = [...this.querySelectorAll('[data-od-next]')];
      this.dots = [...this.querySelectorAll('[data-od-dot]')];

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.prevButtons.forEach((b) => b.addEventListener('click', () => this.go(-1)));
      this.nextButtons.forEach((b) => b.addEventListener('click', () => this.go(1)));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.goTo(i)));

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
      this.update();

      if (this.autoplayMs > 0 && !reduceMotion.matches && this.track.children.length > 1) this.setupAutoplay();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.stopAutoplay?.();
      this.ready = false;
    }

    metrics() {
      const first = this.track.children[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = first ? first.getBoundingClientRect().width + gap : this.track.clientWidth;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap + 2) / step));
      return { step, perView };
    }

    behavior() {
      return reduceMotion.matches ? 'auto' : 'smooth';
    }

    go(direction) {
      const { step, perView } = this.metrics();
      const max = this.track.scrollWidth - this.track.clientWidth;
      let left = this.track.scrollLeft + direction * step * perView;
      if (this.loop) {
        if (direction > 0 && this.track.scrollLeft >= max - 4) left = 0;
        if (direction < 0 && this.track.scrollLeft <= 4) left = max;
      }
      this.track.scrollTo({ left, behavior: this.behavior() });
    }

    goTo(index) {
      const slide = this.track.children[index];
      if (!slide) return;
      const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      this.track.scrollTo({ left: slide.offsetLeft - padding, behavior: this.behavior() });
    }

    update() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      const overflow = max > 4;
      const atStart = t.scrollLeft <= 4;
      const atEnd = t.scrollLeft >= max - 4;
      this.classList.toggle('is-static', !overflow);
      this.prevButtons.forEach((b) => (b.disabled = !overflow || (!this.loop && atStart)));
      this.nextButtons.forEach((b) => (b.disabled = !overflow || (!this.loop && atEnd)));
      if (this.dots.length) {
        const { step } = this.metrics();
        const index = atEnd ? this.dots.length - 1 : Math.min(this.dots.length - 1, Math.round(t.scrollLeft / step));
        this.dots.forEach((d, i) => {
          d.classList.toggle('is-active', i === index);
          d.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }
    }

    setupAutoplay() {
      let timer = null;
      let visible = true;
      let hovering = false;
      const start = () => {
        if (timer || !visible || hovering || document.hidden) return;
        timer = setInterval(() => this.go(1), this.autoplayMs);
      };
      const stop = () => {
        clearInterval(timer);
        timer = null;
      };
      this.stopAutoplay = stop;
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        visible ? start() : stop();
      });
      this.visibilityObserver.observe(this);
      this.addEventListener('mouseenter', () => {
        hovering = true;
        stop();
      });
      this.addEventListener('mouseleave', () => {
        hovering = false;
        start();
      });
      this.addEventListener('focusin', stop);
      this.track.addEventListener('pointerdown', () => {
        stop();
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(start, 8000);
      }, { passive: true });
      document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
      start();
    }
  }
  define('od-carousel', OdCarousel);

  /* -------------------------------------------------------------------
     Contador regresivo
     data-end="AAAA-MM-DDTHH:MM" · data-mode="fixed|daily" · data-tz="-0500"
     ------------------------------------------------------------------- */
  class OdCountdown extends HTMLElement {
    connectedCallback() {
      this.offsetMinutes = this.parseOffset(this.dataset.tz || '-0500');
      this.end = this.computeEnd();
      this.parts = {
        days: this.querySelector('[data-days]'),
        hours: this.querySelector('[data-hours]'),
        minutes: this.querySelector('[data-minutes]'),
        seconds: this.querySelector('[data-seconds]'),
      };
      this.daysUnit = this.parts.days?.closest('.od-countdown__unit');
      this.daysSep = this.querySelector('[data-days-sep]');
      this.tick();
      clearInterval(this.timer);
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    parseOffset(value) {
      const match = /^([+-])(\d{2}):?(\d{2})$/.exec(value.trim());
      if (!match) return -300;
      const minutes = parseInt(match[2], 10) * 60 + parseInt(match[3], 10);
      return match[1] === '-' ? -minutes : minutes;
    }

    computeEnd() {
      const raw = (this.dataset.end || '').trim();
      if (this.dataset.mode !== 'daily' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) {
        const [date, time] = raw.split('T');
        const [y, m, d] = date.split('-').map(Number);
        const [hh, mm] = time.split(':').map(Number);
        return Date.UTC(y, m - 1, d, hh, mm) - this.offsetMinutes * 60000;
      }
      const shifted = new Date(Date.now() + this.offsetMinutes * 60000);
      const nextMidnight = Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate() + 1);
      return nextMidnight - this.offsetMinutes * 60000;
    }

    tick() {
      let diff = this.end - Date.now();
      if (diff <= 0 && this.dataset.mode === 'daily') {
        this.end = this.computeEnd();
        diff = this.end - Date.now();
      }
      diff = Math.max(0, diff);
      const total = Math.floor(diff / 1000);
      const days = Math.floor(total / 86400);
      const pad = (n) => String(n).padStart(2, '0');
      if (this.parts.days) this.parts.days.textContent = pad(days);
      if (this.daysUnit) this.daysUnit.hidden = days === 0;
      if (this.daysSep) this.daysSep.hidden = days === 0;
      if (this.parts.hours) this.parts.hours.textContent = pad(Math.floor((total % 86400) / 3600));
      if (this.parts.minutes) this.parts.minutes.textContent = pad(Math.floor((total % 3600) / 60));
      if (this.parts.seconds) this.parts.seconds.textContent = pad(total % 60);
      if (diff === 0) {
        clearInterval(this.timer);
        this.setAttribute('data-ended', '');
        if (this.hasAttribute('data-hide-on-end') && !(window.Shopify && window.Shopify.designMode)) {
          this.closest('.shopify-section')?.setAttribute('hidden', '');
        }
      }
    }
  }
  define('od-countdown', OdCountdown);

  /* -------------------------------------------------------------------
     Rotador de mensajes (barra de anuncios)
     ------------------------------------------------------------------- */
  class OdRotator extends HTMLElement {
    connectedCallback() {
      this.items = [...this.children];
      if (this.items.length < 2 || this.ready) return;
      this.ready = true;
      this.index = Math.max(0, this.items.findIndex((el) => el.classList.contains('is-active')));
      this.interval = (parseFloat(this.dataset.interval) || 5) * 1000;
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.ready = false;
    }

    start() {
      this.stop();
      this.timer = setInterval(() => this.show(this.index + 1), this.interval);
    }

    stop() {
      clearInterval(this.timer);
    }

    show(next) {
      const count = this.items.length;
      const target = ((next % count) + count) % count;
      this.items.forEach((el, i) => {
        el.classList.toggle('is-active', i === target);
        if (i === target) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', 'true');
      });
      this.index = target;
    }
  }
  define('od-rotator', OdRotator);

  /* -------------------------------------------------------------------
     Header: estado compacto al hacer scroll
     ------------------------------------------------------------------- */
  class OdHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    update() {
      const stickyTop = parseFloat(getComputedStyle(this.section).top) || 0;
      const rect = this.section.getBoundingClientRect();
      const stuck = window.scrollY > 4 && rect.top <= stickyTop + 1;
      this.classList.toggle('is-stuck', stuck);
      const rootStyle = document.documentElement.style;
      rootStyle.setProperty('--od-header-bottom', `${Math.max(0, Math.round(rect.bottom))}px`);
      // Alto que ocupa la cabecera ya fija: lo usan los anclajes para no quedar debajo.
      const sticky = Math.max(0, Math.round(rect.height + stickyTop));
      if (sticky !== this.sticky) {
        this.sticky = sticky;
        rootStyle.setProperty('--od-header-sticky', `${sticky}px`);
      }
    }
  }
  define('od-header', OdHeader);

  /* -------------------------------------------------------------------
     Menú de categorías
     Desktop: megamenú (hover con intención, clic y teclado).
     Mobile: panel lateral con navegación por niveles.
     ------------------------------------------------------------------- */
  class OdMenu extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.scope = this.closest('od-header') || document;
      this.mega = this.querySelector('.od-mega');
      this.drawer = this.querySelector('.od-mdrawer');
      this.toggles = [...this.scope.querySelectorAll('[data-od-menu-toggle]')];
      this.desktopToggle = this.scope.querySelector('.od-header__cats');

      // El panel mobile se mueve al body para quedar por encima de todo.
      if (this.drawer) {
        document.querySelectorAll(`[id="${this.drawer.id}"]`).forEach((el) => el !== this.drawer && el.remove());
        document.body.appendChild(this.drawer);
      }

      this.setupMega();
      this.setupDrawer();

      this.toggles.forEach((button) => button.addEventListener('click', (event) => this.onToggle(event, button)));
      this.onBreakpoint = () => (mqDesktop.matches ? this.closeDrawer(false) : this.closeMega());
      mqDesktop.addEventListener('change', this.onBreakpoint);
      this.onDocumentKey = (event) => {
        if (event.key === 'Escape' && this.megaOpen) this.closeMega();
      };
      document.addEventListener('keydown', this.onDocumentKey);

      this.onBlockSelect = (event) => {
        const panel = event.target.closest && event.target.closest('[data-od-panel]');
        if (!panel || !this.contains(panel) || !mqDesktop.matches) return;
        this.openMega();
        this.activate(this.cats.find((cat) => cat.dataset.odCat === panel.dataset.odPanel));
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.drawer?.remove();
      mqDesktop.removeEventListener('change', this.onBreakpoint);
      document.removeEventListener('keydown', this.onDocumentKey);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.documentElement?.classList.remove('od-lock');
      this.ready = false;
    }

    onToggle(event, button) {
      if (mqDesktop.matches) {
        if (this.megaOpen && Date.now() - this.openedAt > 450) {
          this.closeMega();
        } else {
          this.openMega();
          if (event.detail === 0) requestAnimationFrame(() => this.activeCat?.focus());
        }
      } else if (this.drawerOpen) {
        this.closeDrawer();
      } else {
        this.openDrawer(button);
      }
    }

    setExpanded(open) {
      this.toggles.forEach((button) => {
        button.setAttribute('aria-expanded', String(open));
        button.classList.toggle('is-active', open);
      });
    }

    /* ---------- Desktop ---------- */
    setupMega() {
      if (!this.mega) return;
      this.inner = this.mega.querySelector('.od-mega__inner');
      this.panelsEl = this.mega.querySelector('.od-mega__panels');
      this.cats = [...this.mega.querySelectorAll('[data-od-cat]')];
      this.panels = new Map([...this.mega.querySelectorAll('[data-od-panel]')].map((panel) => [panel.dataset.odPanel, panel]));
      this.activeCat = this.cats.find((cat) => cat.classList.contains('is-active')) || this.cats[0];
      this.points = [];

      const isMouse = (event) => event.pointerType === 'mouse' && finePointer.matches && mqDesktop.matches;
      const scheduleClose = () => {
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.closeMega(), 280);
      };
      const cancelClose = () => clearTimeout(this.closeTimer);

      if (this.desktopToggle) {
        this.desktopToggle.addEventListener('pointerenter', (event) => {
          if (!isMouse(event)) return;
          cancelClose();
          clearTimeout(this.openTimer);
          this.openTimer = setTimeout(() => this.openMega(), 120);
        });
        this.desktopToggle.addEventListener('pointerleave', (event) => {
          if (!isMouse(event)) return;
          clearTimeout(this.openTimer);
          if (this.megaOpen) scheduleClose();
        });
      }

      this.inner.addEventListener('pointerenter', (event) => isMouse(event) && cancelClose());
      this.inner.addEventListener('pointerleave', (event) => isMouse(event) && scheduleClose());
      this.inner.addEventListener(
        'pointermove',
        (event) => {
          this.points.push([event.clientX, event.clientY]);
          if (this.points.length > 4) this.points.shift();
        },
        { passive: true }
      );

      this.cats.forEach((cat) => {
        cat.addEventListener('pointerenter', (event) => isMouse(event) && this.aim(cat));
        cat.addEventListener('focus', () => this.activate(cat));
        cat.addEventListener('keydown', (event) => this.onCatKeydown(event, cat));
        cat.addEventListener('click', (event) => {
          if (cat.getAttribute('href').startsWith('#')) event.preventDefault();
        });
      });

      this.panelsEl.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.activeCat?.focus();
        }
      });

      this.mega.querySelectorAll('[data-od-menu-close]').forEach((el) => el.addEventListener('click', () => this.closeMega()));
      this.mega.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        this.closeMega();
        this.desktopToggle?.focus();
      });
      this.mega.addEventListener('focusout', (event) => {
        const next = event.relatedTarget;
        if (this.megaOpen && next && !this.mega.contains(next) && next !== this.desktopToggle) this.closeMega();
      });
    }

    openMega() {
      if (!this.mega || this.megaOpen || !mqDesktop.matches) return;
      clearTimeout(this.hideTimer);
      clearTimeout(this.closeTimer);
      this.megaOpen = true;
      this.openedAt = Date.now();
      this.mega.hidden = false;
      void this.mega.offsetWidth; // fuerza el reflow para que la transición se ejecute
      this.mega.classList.add('is-open');
      this.setExpanded(true);
    }

    closeMega() {
      if (!this.megaOpen) return;
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      clearTimeout(this.aimTimer);
      this.megaOpen = false;
      this.mega.classList.remove('is-open');
      this.setExpanded(false);
      this.hideTimer = setTimeout(() => {
        if (!this.megaOpen) this.mega.hidden = true;
      }, 260);
    }

    // Evita cambiar de categoría si el mouse va en diagonal hacia el panel.
    aim(cat) {
      clearTimeout(this.aimTimer);
      if (cat === this.activeCat) return;
      if (this.movingTowardPanel()) {
        this.aimTimer = setTimeout(() => cat.matches(':hover') && this.activate(cat), 280);
      } else {
        this.activate(cat);
      }
    }

    movingTowardPanel() {
      if (!this.panelsEl || this.points.length < 2) return false;
      const rect = this.panelsEl.getBoundingClientRect();
      const [x0, y0] = this.points[0];
      const [x1, y1] = this.points[this.points.length - 1];
      if (x1 <= x0 || rect.left - x1 <= 0 || rect.left - x0 <= 0) return false;
      const slope = (x, y, cornerY) => (cornerY - y) / (rect.left - x);
      return slope(x1, y1, rect.top) < slope(x0, y0, rect.top) && slope(x1, y1, rect.bottom) > slope(x0, y0, rect.bottom);
    }

    activate(cat) {
      if (!cat || cat === this.activeCat) return;
      this.activeCat?.classList.remove('is-active');
      cat.classList.add('is-active');
      this.activeCat = cat;
      this.panels.forEach((panel, handle) => {
        panel.hidden = handle !== cat.dataset.odCat;
      });
      if (this.panelsEl) this.panelsEl.scrollTop = 0;
    }

    onCatKeydown(event, cat) {
      const index = this.cats.indexOf(cat);
      let target = null;
      if (event.key === 'ArrowDown') target = this.cats[(index + 1) % this.cats.length];
      else if (event.key === 'ArrowUp') target = this.cats[(index - 1 + this.cats.length) % this.cats.length];
      else if (event.key === 'Home') target = this.cats[0];
      else if (event.key === 'End') target = this.cats[this.cats.length - 1];
      else if (event.key === 'ArrowRight') {
        const first = this.panels.get(cat.dataset.odCat)?.querySelector('a[href], button');
        if (first) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (target) {
        event.preventDefault();
        target.focus();
      }
    }

    /* ---------- Mobile ---------- */
    setupDrawer() {
      if (!this.drawer) return;
      this.sheet = this.drawer.querySelector('.od-mdrawer__sheet');
      this.views = [...this.drawer.querySelectorAll('[data-od-view]')];
      this.rootView = this.views.find((view) => view.dataset.odView === 'root');
      this.views.forEach((view) => (view.inert = view !== this.rootView));

      this.drawer.addEventListener('click', (event) => {
        if (event.target.closest('[data-od-menu-close]')) return this.closeDrawer();
        const opener = event.target.closest('[data-od-open-view]');
        if (opener) return this.showView(opener.dataset.odOpenView, opener);
        if (event.target.closest('[data-od-back]')) return this.showView('root');
      });

      this.drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          if (this.currentView && this.currentView !== this.rootView) this.showView('root');
          else this.closeDrawer();
        } else if (event.key === 'Tab') {
          this.trapFocus(event);
        }
      });
    }

    openDrawer(trigger) {
      if (!this.drawer || this.drawerOpen) return;
      this.drawerOpen = true;
      this.lastTrigger = trigger;
      clearTimeout(this.drawerTimer);
      this.drawer.classList.add('no-anim');
      this.showView('root', null, false);
      this.drawer.hidden = false;
      document.documentElement.classList.add('od-lock');
      void this.drawer.offsetWidth; // fuerza el reflow para que la transición se ejecute
      this.drawer.classList.remove('no-anim');
      this.drawer.classList.add('is-open');
      this.setExpanded(true);
      setTimeout(() => this.drawer.querySelector('.od-mdrawer__close')?.focus({ preventScroll: true }), 80);
    }

    closeDrawer(restoreFocus = true) {
      if (!this.drawerOpen) return;
      this.drawerOpen = false;
      this.drawer.classList.remove('is-open');
      document.documentElement.classList.remove('od-lock');
      this.setExpanded(false);
      this.drawerTimer = setTimeout(() => {
        if (!this.drawerOpen) this.drawer.hidden = true;
      }, 400);
      if (restoreFocus) this.lastTrigger?.focus({ preventScroll: true });
    }

    showView(name, opener = null, moveFocus = true) {
      const target = this.views.find((view) => view.dataset.odView === name) || this.rootView;
      const goingBack = target === this.rootView;
      this.views.forEach((view) => {
        const active = view === target;
        view.classList.toggle('is-active', active);
        view.classList.toggle('is-behind', view === this.rootView && !goingBack);
        view.inert = !active;
      });
      if (!goingBack) {
        this.lastOpener = opener;
        target.scrollTop = 0;
      }
      this.currentView = target;
      if (!moveFocus) return;
      setTimeout(() => {
        const focusTarget = goingBack ? this.lastOpener : target.querySelector('[data-od-back]');
        focusTarget?.focus({ preventScroll: true });
      }, 60);
    }

    trapFocus(event) {
      const focusables = [...this.sheet.querySelectorAll('a[href], button, summary')].filter(
        (el) => !el.closest('[inert]') && el.getClientRects().length > 0
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }
  define('od-menu', OdMenu);

  /* -------------------------------------------------------------------
     Buscador predictivo
     ------------------------------------------------------------------- */
  class OdSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('.od-search__panel');
      this.results = this.querySelector('[data-od-search-results]');
      this.popular = this.querySelector('[data-od-search-popular]');
      this.clearButton = this.querySelector('.od-search__clear');
      this.cancelButton = this.querySelector('.od-search__cancel');
      this.moneyFormat = this.dataset.moneyFormat || 'S/ {{amount}}';
      this.cache = new Map();
      this.activeIndex = -1;
      if (!this.input || !this.panel) return;

      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('click', () => this.open());
      this.input.addEventListener('input', () => {
        this.toggleClear();
        this.search();
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.toggleClear();
        this.renderEmpty();
        this.input.focus();
      });
      this.cancelButton?.addEventListener('click', () => this.close(true));
      this.form.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) {
          event.preventDefault();
          this.input.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (this.isOpen && !this.contains(event.target)) this.close();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.close();
          this.input.blur();
        }
      });
      mqDesktop.addEventListener('change', () => this.isOpen && this.close());
      this.search = debounce(() => this.query(), 200);
      this.toggleClear();
    }

    toggleClear() {
      if (this.clearButton) this.clearButton.hidden = !this.input.value;
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (!mqDesktop.matches) {
        const rect = this.form.getBoundingClientRect();
        this.style.setProperty('--od-search-top', `${Math.round(rect.bottom + 8)}px`);
        document.documentElement.classList.add('od-lock');
      }
      if (this.input.value.trim()) this.query();
    }

    close(reset = false) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      document.documentElement.classList.remove('od-lock');
      if (reset) {
        this.input.value = '';
        this.toggleClear();
        this.renderEmpty();
        this.input.blur();
      }
    }

    async query() {
      const term = this.input.value.trim();
      if (!term) return this.renderEmpty();
      this.open();
      this.classList.add('is-loading');
      const data = await this.fetchResults(term);
      this.classList.remove('is-loading');
      if (term !== this.input.value.trim() || data === null) return;
      this.render(term, data);
    }

    async fetchResults(term) {
      const key = term.toLowerCase();
      if (this.cache.has(key)) return this.cache.get(key);
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const response = await fetch(`${root}search/suggest.json?${params}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const json = await response.json();
        const results = (json.resources && json.resources.results) || {};
        this.cache.set(key, results);
        return results;
      } catch (error) {
        return error.name === 'AbortError' ? null : {};
      }
    }

    formatMoney(value) {
      let amount = 0;
      if (typeof value === 'string' && value.includes('.')) amount = parseFloat(value);
      else amount = Number(value || 0) / 100;
      const withDelimiters = (number, decimals, thousands, decimal) => {
        const [int, frac] = number.toFixed(decimals).split('.');
        return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
      };
      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return withDelimiters(amount, 0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelimiters(amount, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiters(amount, 0, '.', ',');
          default:
            return withDelimiters(amount, 2, ',', '.');
        }
      });
    }

    imageUrl(product) {
      const url = (product.featured_image && product.featured_image.url) || product.image;
      if (!url) return '';
      return `${url}${url.includes('?') ? '&' : '?'}width=120`;
    }

    renderEmpty() {
      this.results.innerHTML = '';
      if (this.popular) this.popular.hidden = false;
      this.activeIndex = -1;
    }

    render(term, data) {
      const safeTerm = escapeHTML(term);
      const products = data.products || [];
      const collections = (data.collections || []).slice(0, 4);
      const queries = (data.queries || []).filter((q) => q.text.toLowerCase() !== term.toLowerCase()).slice(0, 4);
      const searchUrl = `${root}search?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last`;
      let html = '';

      if (queries.length || collections.length) {
        html += '<div class="od-search__block"><p class="od-search__label">Sugerencias</p><div class="od-search__chips">';
        html += queries
          .map((q) => `<a class="od-search__chip" data-od-option href="${q.url}">${q.styled_text || escapeHTML(q.text)}</a>`)
          .join('');
        html += collections
          .map((c) => `<a class="od-search__chip od-search__chip--collection" data-od-option href="${c.url}">${escapeHTML(c.title)}</a>`)
          .join('');
        html += '</div></div>';
      }

      if (products.length) {
        html += '<div class="od-search__block"><p class="od-search__label">Productos</p><div class="od-search__list">';
        html += products
          .map((p) => {
            const image = this.imageUrl(p);
            const onSale = parseFloat(p.compare_at_price_max) > parseFloat(p.price);
            return `<a class="od-search__item" data-od-option href="${p.url}">
              <span class="od-search__thumb">${image ? `<img src="${image}" alt="" loading="lazy" width="56" height="56">` : ''}</span>
              <span class="od-search__info">
                <span class="od-search__name">${escapeHTML(p.title)}</span>
                <span class="od-search__meta">
                  <span class="od-search__price">${this.formatMoney(p.price)}</span>
                  ${onSale ? `<s class="od-search__was">${this.formatMoney(p.compare_at_price_max)}</s>` : ''}
                  ${p.available === false ? '<span class="od-search__soldout">Agotado</span>' : ''}
                </span>
              </span>
            </a>`;
          })
          .join('');
        html += '</div></div>';
      } else {
        html += `<p class="od-search__empty">No encontramos productos para “${safeTerm}”. Prueba con otra palabra o con el código del producto.</p>`;
      }

      html += `<a class="od-search__all" data-od-option href="${searchUrl}">Ver todos los resultados para “${safeTerm}”</a>`;

      if (this.popular) this.popular.hidden = true;
      this.results.innerHTML = html;
      this.options = [...this.results.querySelectorAll('[data-od-option]')];
      this.options.forEach((el, i) => (el.id = `${this.input.id}-opt-${i}`));
      this.activeIndex = -1;
      announce(products.length ? `${products.length} productos sugeridos` : 'Sin productos sugeridos');
    }

    onKeydown(event) {
      if (!this.isOpen || !this.options || !this.options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + delta + this.options.length) % this.options.length;
        this.options.forEach((el, i) => el.classList.toggle('is-active', i === this.activeIndex));
        const active = this.options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex > -1) {
        event.preventDefault();
        window.location.href = this.options[this.activeIndex].href;
      }
    }
  }
  define('od-search', OdSearch);

  /* -------------------------------------------------------------------
     Tarjeta: galería al pasar el mouse (solo puntero fino)
     ------------------------------------------------------------------- */
  const setupGallery = (card) => {
    if (card.odGallery) return;
    const media = card.querySelector('[data-od-gallery]');
    if (!media) return;
    card.odGallery = true;
    const images = [...media.querySelectorAll('.od-card__img')];
    const dots = [...media.querySelectorAll('.od-card__dot')];
    images.forEach((img) => {
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
    });
    let current = 0;
    const show = (index) => {
      if (index === current) return;
      current = index;
      images.forEach((img, i) => img.classList.toggle('is-active', i === index));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };
    card.addEventListener('mousemove', (event) => {
      const rect = media.getBoundingClientRect();
      if (event.clientY < rect.top || event.clientY > rect.bottom) return;
      const ratio = (event.clientX - rect.left) / rect.width;
      show(Math.min(images.length - 1, Math.max(0, Math.floor(ratio * images.length))));
    });
    card.addEventListener('mouseleave', () => show(0));
  };

  document.addEventListener(
    'pointerover',
    (event) => {
      if (!finePointer.matches || event.pointerType !== 'mouse') return;
      const card = event.target.closest && event.target.closest('[data-od-card]');
      if (card) setupGallery(card);
    },
    { passive: true }
  );

  /* -------------------------------------------------------------------
     Copiar código (SKU)
     ------------------------------------------------------------------- */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest && event.target.closest('[data-od-copy]');
    if (!button) return;
    event.preventDefault();
    const value = button.dataset.odCopy;
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
      document.body.appendChild(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }
    button.classList.add('is-copied');
    announce(`Código ${value} copiado`);
    clearTimeout(button.odTimer);
    button.odTimer = setTimeout(() => button.classList.remove('is-copied'), 1600);
  });

  /* -------------------------------------------------------------------
     Texto expandible ("Leer más")
     ------------------------------------------------------------------- */
  const setupExpandables = () => {
    document.querySelectorAll('[data-od-expand]').forEach((button) => {
      const body = document.getElementById(button.getAttribute('aria-controls'));
      if (!body || button.getAttribute('aria-expanded') === 'true') return;
      const needsToggle = body.scrollHeight > body.clientHeight + 12;
      button.hidden = !needsToggle;
      body.classList.toggle('is-collapsible', needsToggle);
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-od-expand]');
    if (!button) return;
    const body = document.getElementById(button.getAttribute('aria-controls'));
    if (!body) return;
    const expand = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(expand));
    body.style.maxHeight = expand ? `${body.scrollHeight}px` : '';
    body.classList.toggle('is-expanded', expand);
    const label = button.querySelector('[data-label]');
    if (label) label.textContent = expand ? button.dataset.less : button.dataset.more;
  });

  /* -------------------------------------------------------------------
     Filas deslizables: quitar el degradado al llegar al final
     ------------------------------------------------------------------- */
  const setupScrollers = () => {
    document.querySelectorAll('.od-hnav__scroller').forEach((el) => {
      if (el.odScroller) return;
      el.odScroller = true;
      const update = () => el.classList.toggle('is-end', el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
      el.addEventListener('scroll', update, { passive: true });
      new ResizeObserver(update).observe(el);
      update();
    });
  };

  /* -------------------------------------------------------------------
     Acordeones del footer: cerrados en mobile, abiertos en desktop
     ------------------------------------------------------------------- */
  const syncAccordions = () => {
    document.querySelectorAll('details[data-od-accordion]').forEach((el) => {
      el.open = !mqMobile.matches;
    });
  };

  document.addEventListener('click', (event) => {
    const summary = event.target.closest && event.target.closest('details[data-od-accordion] > summary');
    if (summary && !mqMobile.matches) event.preventDefault();
  });

  /* -------------------------------------------------------------------
     Editor de temas: mostrar el bloque seleccionado en carruseles
     ------------------------------------------------------------------- */
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target;
    const carousel = slide.closest && slide.closest('od-carousel');
    const track = carousel && carousel.querySelector('[data-od-track]');
    if (!track) return;
    const index = [...track.children].indexOf(slide.closest('[data-od-track] > *'));
    if (index > -1) carousel.goTo(index);
  });

  const init = () => {
    syncAccordions();
    setupExpandables();
    setupScrollers();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', setupExpandables);
  mqMobile.addEventListener('change', syncAccordions);
  document.addEventListener('shopify:section:load', init);
})();
